import React, { useContext, useEffect, useState } from "react";

import NotificationBar from "../notification-bar";

import { AttributesContext } from "../../contexts/attributes";
import eventEmitter from "../../shared/events";
import { validateInput } from "../../shared/validation";
import { lookupUUID } from "../../shared/attribute-uuid";
import { useValidateAttributePair } from "../../shared/hooks/use-validate-attribute-pair";

const PACKAGE_MARKETING_COPY_UUID = lookupUUID("packageMarketingCopy");

const AttributeErrors = ({ attributeGroups, attributesByGroup, attributesSubgroups, readOnlyOverride, showValidation, allowNoAttributes }) => {
    const [attributeErrors, setAttributeErrors] = useState([]);
    const attributes = useContext(AttributesContext);
    const validatePair = useValidateAttributePair();

    useEffect(() => {
        if (allowNoAttributes) {
            eventEmitter.emit("ATTRIBUTES_VALIDITY", true, true);
            return;
        }

        const newAttributeErrors =
            attributes.data.length === 0
                ? [{ name: "More than 1 attribute is required", errors: [] }]
                : attributeGroups.reduce((errArr, group) => {
                      const groupErrors = attributesByGroup[group.name].reduce(
                          (arr, { uuid, name, validation, pairedAttributes, readOnly }, index) => {
                              let isPairInvalid = false;

                              if (pairedAttributes) {
                                  const [pairedAttribute] = pairedAttributes;

                                  if (pairedAttribute && pairedAttribute.required) {
                                      isPairInvalid = validatePair(
                                          { value: attributes.data[uuid] },
                                          { value: attributes.data[pairedAttribute.uuid] }
                                      );
                                  }
                              }

                              const validAttribute = validateInput(attributes.data[uuid], validation);
                              const ignoreField = !readOnlyOverride && readOnly;

                              if (attributes.data.hasOwnProperty(uuid) && !validAttribute.isValid && !ignoreField) {
                                  arr.push(<li key={`${group.name}_${index}`}>{name}</li>);
                              } else if (isPairInvalid) {
                                  arr.push(<li key={`${group.name}_${index}`}>{name}</li>);
                              }

                              return arr;
                          },
                          []
                      );

                      if (groupErrors.length > 0) {
                          errArr.push({
                              name: group.label,
                              errors: groupErrors,
                          });
                      }

                      return errArr;
                  }, []);

        const hasEmptyPackageMarketingCopy =
            attributes.data.hasOwnProperty(PACKAGE_MARKETING_COPY_UUID) && !attributes.data[PACKAGE_MARKETING_COPY_UUID];

        if (!attributesSubgroups) {
            setAttributeErrors(newAttributeErrors);
            eventEmitter.emit("ATTRIBUTES_VALIDITY", newAttributeErrors.length === 0, hasEmptyPackageMarketingCopy);
        } else {
            const filteredSubgroups = validateSubgroups();
            setAttributeErrors([...newAttributeErrors, ...filteredSubgroups]);
            eventEmitter.emit("ATTRIBUTES_VALIDITY", newAttributeErrors.length === 0 && filteredSubgroups.length === 0, hasEmptyPackageMarketingCopy);
        }
    }, [attributesByGroup, attributesSubgroups, attributes.data]);

    const extractAttrs = (attrsGroups) => {
        const attrs = attrsGroups.reduce((array, group) => {
            if (!_.isEmpty(group.subGroups)) {
                const inner = extractAttrs(group.subGroups);
                array.push(inner);

                return array;
            }

            array.push(group.attributes);

            return array;
        }, []);

        return attrs;
    };

    const validateSubgroups = () => {
        return attributeGroups.reduce((arr, group) => {
            if (!attributesSubgroups[group.name]) {
                return arr;
            }
            const errors = extractAttrs(attributesSubgroups[group.name]);

            const flattenAttr = errors.flat(Infinity);

            const groupErrors = flattenAttr.reduce((array, { uuid, name, validation, pairedAttributes, readOnly }, index) => {
                let isPairInvalid = false;

                if (pairedAttributes) {
                    const [pairedAttribute] = pairedAttributes;

                    if (pairedAttribute && pairedAttribute.required) {
                        isPairInvalid = validatePair({ value: attributes.data[uuid] }, { value: attributes.data[pairedAttribute.uuid] });
                    }
                }

                const validAttribute = validateInput(attributes.data[uuid], validation);
                const ignoreField = !readOnlyOverride && readOnly;

                if (attributes.data.hasOwnProperty(uuid) && !validAttribute.isValid && !ignoreField) {
                    {
                        array.push(
                            <li
                                name={group.name}
                                key={`${group.name}_${index}`}
                            >
                                {name}
                            </li>
                        );
                    }
                } else if (isPairInvalid) {
                    array.push(
                        <li
                            name={group.name}
                            key={`${group.name}_${index}`}
                        >
                            {name}
                        </li>
                    );
                } else if (!attributes.data.hasOwnProperty(uuid) && !validAttribute.isValid) {
                    array.push(
                        <li
                            name={group.name}
                            key={`${group.name}_${index}`}
                        >
                            {name}
                        </li>
                    );
                }

                return array;
            }, []);

            const flatArr = groupErrors.flat(Infinity);

            if (flatArr.length > 0) {
                const groupObject = {
                    name: `Required ${group.label}`,
                    errors: flatArr,
                };
                arr.push(groupObject);
            }

            return arr;
        }, []);
    };

    const errorBody = attributeErrors.map((e, index) => {
        const listClassOverride = attributesSubgroups ? "p-l-14" : "";
        const errors = e?.errors?.length > 0 ? <ul className={listClassOverride}>{e.errors}</ul> : null;

        return (
            <div key={`error_${index}`}>
                <h4>{e.name}</h4> {errors}
            </div>
        );
    });

    return showValidation && attributeErrors.length > 0 ? <NotificationBar type="error">{errorBody}</NotificationBar> : null;
};

export default AttributeErrors;
